import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Input,
    Label,
    Spinner,
    Badge,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import RecouvrementDetailsDrawer from "./RecouvrementDetailsDrawer";
import RecouvrementSummaryTable from "./RecouvrementSummaryTable";
import { recouvrementActions } from "../../sagas/recouvrementSlice";

/** ========= small date utils (no libs) ========= */
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const startOfMonth = (y, m) => new Date(y, m, 1);
const endOfMonth = (y, m) => new Date(y, m + 1, 0);
const addMonths = (d, diff) => new Date(d.getFullYear(), d.getMonth() + diff, 1);
const sameDayISO = (iso, d) => iso === toISO(d);

const monthLabel = (d) =>
    d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

/** Build a 6x7 grid (Sun..Sat) for the month */
function buildMonthGrid(viewDate) {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    const first = startOfMonth(y, m);
    const last = endOfMonth(y, m);

    // JS: 0=Sun..6=Sat. We want Monday as first column for FR UI.
    const firstDow = (first.getDay() + 6) % 7; // 0=Mon..6=Sun
    const totalDays = last.getDate();

    const cells = [];
    // previous month fillers
    const prevLast = endOfMonth(y, m - 1);
    for (let i = 0; i < firstDow; i++) {
        const day = prevLast.getDate() - (firstDow - 1 - i);
        cells.push(new Date(y, m - 1, day));
    }
    // current month
    for (let day = 1; day <= totalDays; day++) {
        cells.push(new Date(y, m, day));
    }
    // next month fillers to reach 42 cells
    while (cells.length < 42) {
        const nextDay = cells.length - (firstDow + totalDays) + 1;
        cells.push(new Date(y, m + 1, nextDay));
    }

    // chunk weeks
    const weeks = [];
    for (let i = 0; i < 6; i++) weeks.push(cells.slice(i * 7, i * 7 + 7));
    return weeks;
}

const fmtMoney = (n) => {
    const x = Number(n || 0);
    return x.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const RecouvrementCalendarPage = () => {
    const dispatch = useDispatch();
    const {
        summaryAll,
        summaryPending,
        details,
        selectedDate,
        loadingSummaryAll,
        loadingSummaryPending,
        loadingDetails,
        exporting,
        error,
        dateDebut,
        dateFin,
        clientId,
        banque,
        query,
    } = useSelector((s) => s.recouvrement);

    const [viewDate, setViewDate] = useState(() => new Date());
    const [drawerOpen, setDrawerOpen] = useState(false);

    // local filters (UI friendly)
    const [fClientId, setFClientId] = useState(clientId || "");
    const [fBanque, setFBanque] = useState(banque || "");
    const [fQuery, setFQuery] = useState(query || "");
    const [localSelectedDate, setLocalSelectedDate] = useState(null);

    const monthStart = useMemo(() => startOfMonth(viewDate.getFullYear(), viewDate.getMonth()), [viewDate]);
    const monthEnd = useMemo(() => endOfMonth(viewDate.getFullYear(), viewDate.getMonth()), [viewDate]);

    const asISODate = (v) => (v ? String(v).slice(0, 10) : null);

    const summaryMapAll = useMemo(() => {
        const map = new Map();
        (summaryAll || []).forEach((x) => {
            const key = asISODate(x.dateEcheance);
            if (key) map.set(key, x);
        });
        return map;
    }, [summaryAll]);

    const summaryMapPending = useMemo(() => {
        const map = new Map();
        (summaryPending || []).forEach((x) => {
            const key = asISODate(x.dateEcheance);
            if (key) map.set(key, x);
        });
        return map;
    }, [summaryPending]);

    const weeks = useMemo(() => buildMonthGrid(viewDate), [viewDate]);

    const kpis = useMemo(() => {
        const todayISO = toISO(new Date());

        const sum = (arr) =>
            (arr || []).reduce(
                (acc, x) => {
                    acc.total += Number(x.total || 0);
                    acc.count += Number(x.count || 0);
                    return acc;
                },
                { total: 0, count: 0 }
            );

        const all = sum(summaryAll);
        const pending = sum(summaryPending);

        const overdue = (summaryPending || []).reduce(
            (acc, x) => {
                const d = asISODate(x.dateEcheance);
                if (d && d < todayISO) {
                    acc.total += Number(x.total || 0);
                    acc.count += Number(x.count || 0);
                }
                return acc;
            },
            { total: 0, count: 0 }
        );

        return {
            totalAll: all.total,
            countAll: all.count,
            totalPending: pending.total,
            countPending: pending.count,
            overdueTotal: overdue.total,
            overdueCount: overdue.count,
        };
    }, [summaryAll, summaryPending]);

    const fetchMonthSummary = () => {
        const params = {
            dateDebut: toISO(monthStart),
            dateFin: toISO(monthEnd),
            clientId: fClientId ? Number(fClientId) : null,
            banque: fBanque || null,
            query: fQuery || null,
        };

        dispatch(recouvrementActions.fetchSummaryAll(params));
        dispatch(recouvrementActions.fetchSummaryPending(params));
    };

    useEffect(() => {
        // initial load + whenever month changes
        fetchMonthSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewDate]);

    const openDetails = (dateISO) => {
        if (!dateISO) return;
        setLocalSelectedDate(dateISO);
        dispatch(
            recouvrementActions.fetchDetails({
                dateEcheance: dateISO,
                clientId: fClientId ? Number(fClientId) : null,
                banque: fBanque ? fBanque : null,
                query: fQuery ? fQuery : null,
                statut: null, // IMPORTANT: calendar wants EN_ATTENTE + ENCAISSE
            })
        );

        setDrawerOpen(true);
    };

    const onPrev = () => setViewDate((d) => addMonths(d, -1));
    const onNext = () => setViewDate((d) => addMonths(d, +1));
    const onToday = () => setViewDate(new Date());

    const applyFilters = () => {
        fetchMonthSummary({
            clientId: fClientId ? Number(fClientId) : null,
            banque: fBanque ? fBanque : null,
            query: fQuery ? fQuery : null,
        });
    };

    const resetFilters = () => {
        setFClientId("");
        setFBanque("");
        setFQuery("");

        const params = {
            dateDebut: toISO(monthStart),
            dateFin: toISO(monthEnd),
            clientId: null,
            banque: null,
            query: null,
        };

        dispatch(recouvrementActions.fetchSummaryAll(params));
        dispatch(recouvrementActions.fetchSummaryPending(params));
    };

    const exportExcel = () => {
        dispatch(recouvrementActions.exportExcel());
    };

    return (
        <div className="page-content">
            {/* ===== Top KPIs + Actions ===== */}
            <Row className="g-2">
                <Col lg="8">
                    <Card className="shadow-sm border-0" style={{ borderRadius: 16 }}>
                        <CardBody style={{ padding: 12 }}>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <div>
                                    <div style={{ fontSize: 12, color: "#6b7280" }}>Total chèques (période)</div>
                                    <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.2 }}>
                                        {monthLabel(viewDate)}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <Button color="light" size="sm" onClick={onPrev} className="px-2" style={{ borderRadius: 10 }}>
                                        ←
                                    </Button>
                                    <Button color="light" size="sm" onClick={onToday} className="px-2" style={{ borderRadius: 10 }}>
                                        Aujourd’hui
                                    </Button>
                                    <Button color="light" size="sm" onClick={onNext} className="px-2" style={{ borderRadius: 10 }}>
                                        →
                                    </Button>

                                    <div className="vr d-none d-md-block" />

                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={exportExcel}
                                        disabled={exporting}
                                        className="px-2"
                                        style={{ borderRadius: 10 }}
                                    >
                                        {exporting ? (
                                            <>
                                                <Spinner size="sm" className="me-2" /> Export…
                                            </>
                                        ) : (
                                            "Exporter Excel"
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {/* KPI cards */}
                            <Row className="mt-2 g-2">
                                <Col md="6" xl="3">
                                    <div
                                        className="p-2"
                                        style={{
                                            borderRadius: 12,
                                            background: "#f8fafc",
                                            border: "1px solid #e5e7eb",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                            Total chèques (période)
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 900 }}>{fmtMoney(kpis.totalAll)} DH</div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{kpis.countAll} chèque(s)</div>
                                    </div>
                                </Col>
                                <Col md="6" xl="3">
                                    <div
                                        className="p-2"
                                        style={{
                                            borderRadius: 12,
                                            background: "#d2f3e3",
                                            border: "1px solid #fed7aa",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#065f46" }}>
                                            À encaisser
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 900, color: "#065f46" }}>
                                            {fmtMoney(kpis.totalPending)} MAD
                                        </div>
                                        <div style={{ fontSize: 12, color: "#065f46" }}>
                                            {kpis.countPending} chèque(s)
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6" xl="3">
                                    <div
                                        className="p-2"
                                        style={{
                                            borderRadius: 12,
                                            background: "#fff7ed",
                                            border: "1px solid #fed7aa",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#9a3412" }}>Échus (en retard)</div>
                                        <div style={{ fontSize: 16, fontWeight: 900, color: "#9a3412" }}>
                                            {fmtMoney(kpis.overdueTotal)} DH
                                        </div>
                                        <div style={{ fontSize: 12, color: "#9a3412" }}>{kpis.overdueCount} chèque(s)</div>
                                    </div>
                                </Col>

                                <Col md="6" xl="3">
                                    <div
                                        className="p-2"
                                        style={{
                                            borderRadius: 12,
                                            background: "#eef2ff",
                                            border: "1px solid #c7d2fe",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#3730a3" }}>Période</div>
                                        <div style={{ fontSize: 12, fontWeight: 800 }}>
                                            {toISO(monthStart)} → {toISO(monthEnd)}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>Date d’échéance</div>
                                    </div>
                                </Col>

                                {/* <Col md="6" xl="3">
                                    <div
                                        className="p-3"
                                        style={{
                                            borderRadius: 14,
                                            background: "#ecfdf5",
                                            border: "1px solid #bbf7d0",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#065f46" }}>Filtrage</div>
                                        <div style={{ fontSize: 12, color: "#065f46" }}>
                                            {fClientId ? `Client #${fClientId}` : "Tous clients"}
                                            {fBanque ? ` · ${fBanque}` : ""}
                                            {fQuery ? ` · “${fQuery}”` : ""}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>Appliqué au calendrier + tableau</div>
                                    </div>
                                </Col> */}
                            </Row>

                            {/* Filters */}
                            {/* <Row className="mt-3 g-3">
                                <Col md="3">
                                    <Label className="mb-1" style={{ fontSize: 12, color: "#6b7280" }}>
                                        Client (ID)
                                    </Label>
                                    <Input
                                        value={fClientId}
                                        onChange={(e) => setFClientId(e.target.value)}
                                        placeholder="ex: 12"
                                        style={{ borderRadius: 12 }}
                                    />
                                </Col>
                                <Col md="3">
                                    <Label className="mb-1" style={{ fontSize: 12, color: "#6b7280" }}>
                                        Banque
                                    </Label>
                                    <Input
                                        value={fBanque}
                                        onChange={(e) => setFBanque(e.target.value)}
                                        placeholder="ex: CIH, BMCI..."
                                        style={{ borderRadius: 12 }}
                                    />
                                </Col>
                                <Col md="4">
                                    <Label className="mb-1" style={{ fontSize: 12, color: "#6b7280" }}>
                                        Recherche
                                    </Label>
                                    <Input
                                        value={fQuery}
                                        onChange={(e) => setFQuery(e.target.value)}
                                        placeholder="Client, n° chèque, facture, BL..."
                                        style={{ borderRadius: 12 }}
                                    />
                                </Col>
                                <Col md="2" className="d-flex align-items-end gap-2">
                                    <Button color="primary" onClick={applyFilters} style={{ borderRadius: 12 }} className="w-100">
                                        Appliquer
                                    </Button>
                                    <Button color="light" onClick={resetFilters} style={{ borderRadius: 12 }} className="w-100">
                                        Reset
                                    </Button>
                                </Col>
                            </Row> */}

                            {error ? (
                                <div className="mt-3" style={{ color: "#b91c1c", fontSize: 13 }}>
                                    {error}
                                </div>
                            ) : null}
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="4">
                    <Card className="shadow-sm border-0" style={{ borderRadius: 16 }}>
                        <CardBody style={{ padding: 12 }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div style={{ fontWeight: 900, fontSize: 16 }}>Vue Direction</div>
                                {loadingSummaryAll ? (
                                    <Badge color="light" className="px-2 py-1">
                                        <Spinner size="sm" className="me-2" /> Chargement…
                                    </Badge>
                                ) : (
                                    <Badge color="light" className="px-2 py-1">
                                        {summaryAll?.length || 0} date(s)
                                    </Badge>
                                )}
                            </div>

                            <div className="mt-2" style={{ fontSize: 12, color: "#6b7280" }}>
                                Cliquez sur un <strong>montant</strong> pour afficher les chèques détaillés.
                            </div>

                            <div className="mt-2" style={{ maxHeight: 280, overflow: "auto" }}>
                                <RecouvrementSummaryTable
                                    data={summaryAll || []}
                                    loading={loadingSummaryAll}
                                    onSelectDate={(dateISO) => openDetails(dateISO)}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ===== Calendar ===== */}
            <Row className="mt-2">
                <Col>
                    <Card className="shadow-sm border-0" style={{ borderRadius: 16 }}>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <div style={{ fontWeight: 900, fontSize: 16 }}>Calendrier des échéances</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    Totaux par date · Vue mensuelle · Clic = détails
                                </div>
                            </div>

                            <div className="mt-3" style={{ border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
                                {/* Calendar header */}
                                <div
                                    className="d-grid"
                                    style={{
                                        gridTemplateColumns: "repeat(7, 1fr)",
                                        background: "#f8fafc",
                                        borderBottom: "1px solid #e5e7eb",
                                    }}
                                >
                                    {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
                                        <div key={d} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 800, color: "#334155" }}>
                                            {d}
                                        </div>
                                    ))}
                                </div>

                                {/* Calendar body */}
                                <div
                                    className="d-grid"
                                    style={{
                                        gridTemplateColumns: "repeat(7, 1fr)",
                                        gridAutoRows: "minmax(96px, auto)",
                                    }}
                                >
                                    {weeks.flat().map((day, idx) => {
                                        const inMonth = day.getMonth() === viewDate.getMonth();
                                        const iso = toISO(day);
                                        const cellAll = summaryMapAll.get(iso);
                                        const totalAll = cellAll ? Number(cellAll.total || 0) : 0;
                                        const countAll = cellAll ? Number(cellAll.count || 0) : 0;

                                        const cellPending = summaryMapPending.get(iso);
                                        const totalPending = cellPending ? Number(cellPending.total || 0) : 0;
                                        const countPending = cellPending ? Number(cellPending.count || 0) : 0;

                                        const todayISO = toISO(new Date());
                                        const isToday = iso === todayISO;
                                        const isOverdue = iso < todayISO && totalPending > 0;

                                        return (
                                            <div
                                                key={`${iso}-${idx}`}
                                                style={{
                                                    padding: 10,
                                                    borderRight: (idx + 1) % 7 === 0 ? "none" : "1px solid #e5e7eb",
                                                    borderBottom: idx < 35 ? "1px solid #e5e7eb" : "none",
                                                    background: inMonth ? "#ffffff" : "#f9fafb",
                                                    position: "relative",
                                                }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 900,
                                                            color: inMonth ? "#0f172a" : "#94a3b8",
                                                        }}
                                                    >
                                                        {day.getDate()}
                                                    </div>

                                                    {isToday ? (
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                fontWeight: 900,
                                                                padding: "2px 8px",
                                                                borderRadius: 999,
                                                                background: "#e0f2fe",
                                                                color: "#075985",
                                                            }}
                                                        >
                                                            Aujourd’hui
                                                        </span>
                                                    ) : null}
                                                </div>

                                                {loadingSummaryAll || loadingSummaryPending ? (
                                                    <div className="mt-2" style={{ fontSize: 12, color: "#94a3b8" }}>
                                                        <Spinner size="sm" className="me-2" /> …
                                                    </div>
                                                ) : totalAll > 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openDetails(iso)}
                                                        style={{
                                                            marginTop: 10,
                                                            width: "100%",
                                                            textAlign: "left",
                                                            border: "1px solid " + (isOverdue ? "#fed7aa" : "#e5e7eb"),
                                                            background: isOverdue ? "#fff7ed" : "#ffffff",
                                                            borderRadius: 12,
                                                            padding: "10px 10px",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        {/* Total (ALL) */}
                                                        <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a" }}>
                                                            {fmtMoney(totalAll)} MAD
                                                        </div>
                                                        <div style={{ fontSize: 11, color: "#64748b" }}>
                                                            {countAll} chèque(s) (total)
                                                        </div>

                                                        {/* Pending */}
                                                        <div style={{ marginTop: 6, fontSize: 11, color: isOverdue ? "#9a3412" : "#065f46", fontWeight: 800 }}>
                                                            À encaisser: {fmtMoney(totalPending)} MAD
                                                        </div>
                                                        <div style={{ fontSize: 11, color: isOverdue ? "#9a3412" : "#065f46" }}>
                                                            {countPending} chèque(s)
                                                        </div>
                                                    </button>
                                                ) : (
                                                    <div style={{ marginTop: 14, fontSize: 12, color: "#94a3b8" }}>—</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mt-3" style={{ fontSize: 12, color: "#6b7280" }}>
                                Astuce : la direction voit le **total par date**. Pour les détails, un simple clic sur le montant suffit.
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ===== Drawer details ===== */}
            <RecouvrementDetailsDrawer
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                dateISO={localSelectedDate}
                items={details || []}
                loading={loadingDetails}
                onExport={() => exportExcel()}
                exporting={exporting}
            />
        </div>
    );
};

export default RecouvrementCalendarPage;
